import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatINR } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { TrendingUp, IndianRupee, AlertCircle, Loader2, Share2, Download, Copy, Check, Receipt, MessageCircle, FilePlus, Printer, ScanLine, Send } from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppLink, TEMPLATES, wa } from "../lib/whatsapp";
import { MethodPicker, PAYMENT_METHODS } from "../components/QuickBillDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import UpiQrDialog from "../components/UpiQrDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [clinicName, setClinicName] = useState("our clinic");
  const [upiVpa, setUpiVpa] = useState("");
  const [upiPayeeName, setUpiPayeeName] = useState("");
  const [waConfigured, setWaConfigured] = useState(false);
  const [patientPhones, setPatientPhones] = useState({});
  const [payMethodFor, setPayMethodFor] = useState(null);
  const [qrInvoice, setQrInvoice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [inv, sum] = await Promise.all([
        api.get("/invoices", { params: { limit: 200 } }),
        api.get("/billing/summary"),
      ]);
      setInvoices(inv.data.items || inv.data);
      setSummary(sum.data);
    } catch (e) {
      toast.error("Failed to load billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Fetch clinic name + patient phones once
  useEffect(() => {
    api.get("/clinic/settings").then(({ data }) => {
      setClinicName(data?.name || "our clinic");
      setUpiVpa(data?.upi_vpa || "");
      setUpiPayeeName(data?.upi_payee_name || data?.name || "");
    }).catch(() => {});
    wa.status().then((s) => setWaConfigured(!!s.configured)).catch(() => {});
    api.get("/patients", { params: { limit: 500 } }).then(({ data }) => {
      const list = data.items || data;
      const map = {};
      list.forEach((p) => { map[p.patient_id] = p.phone; });
      setPatientPhones(map);
    }).catch(() => {});
  }, []);

  const duplicateInvoice = async (inv) => {
    try {
      await api.post(`/invoices/${inv.invoice_id}/duplicate`);
      toast.success("Invoice duplicated");
      load();
    } catch (e) {
      toast.error("Could not duplicate");
    }
  };

  const sendInvoiceWhatsApp = async (inv) => {
    const phone = patientPhones[inv.patient_id];
    if (!phone) {
      toast.error("Patient phone is missing");
      return;
    }
    try {
      const { data } = await api.get(`/invoices/${inv.invoice_id}/share`);
      const link = `${window.location.origin}${data.share_path}`;
      const msg = TEMPLATES.invoiceShare({
        patient_name: inv.patient_name,
        clinic_name: clinicName,
        amount: inv.amount,
        link,
      });
      window.open(buildWhatsAppLink(phone, msg), "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error("Failed to prepare WhatsApp share");
    }
  };

  const togglePaid = async (inv) => {
    if (inv.status === "paid") {
      try {
        await api.put(`/invoices/${inv.invoice_id}/status`, { status: "unpaid" });
        toast.success("Marked as unpaid");
        load();
      } catch (e) {
        toast.error("Failed");
      }
    } else {
      // Open method picker
      setPayMethodFor(inv);
    }
  };

  const markPaidWithMethod = async (inv, method) => {
    try {
      await api.put(`/invoices/${inv.invoice_id}/status`, { status: "paid", payment_method: method });
      toast.success(`Marked paid · ${method.toUpperCase()}`);
      setPayMethodFor(null);
      load();
    } catch (e) {
      toast.error("Failed to mark paid");
    }
  };

  const printInvoice = (invoice_id) => {
    const url = `${BACKEND_URL}/api/invoices/${invoice_id}/pdf`;
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const w = window.open(u, "_blank");
        if (w) w.addEventListener("load", () => { try { w.print(); } catch {} });
      })
      .catch(() => toast.error("Print failed"));
  };

  const copyShareLink = async (invoice_id) => {
    try {
      const { data } = await api.get(`/invoices/${invoice_id}/share`);
      const url = `${window.location.origin}${data.share_path}`;
      await navigator.clipboard.writeText(url);
      setCopiedId(invoice_id);
      toast.success("Share link copied to clipboard");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      toast.error("Failed to copy link");
    }
  };

  const downloadPdf = (invoice_id) => {
    const url = `${BACKEND_URL}/api/invoices/${invoice_id}/pdf`;
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `invoice-${invoice_id.slice(-8)}.pdf`;
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("PDF download failed"));
  };

  const downloadDocx = (invoice_id) => {
    const url = `${BACKEND_URL}/api/invoices/${invoice_id}/docx`;
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `invoice-${invoice_id.slice(-8)}.docx`;
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("DOCX download failed"));
  };

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-in" data-testid="billing-page">
      <div className="mb-8 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-600 mb-2">
            Finance
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Billing
          </h1>
          <p className="text-slate-600 mt-1">Track invoices and revenue.</p>
        </div>
        <Link to="/billing/new">
          <Button className="bg-blue-600 hover:bg-blue-700 h-11" data-testid="open-billing-new">
            <FilePlus className="w-4 h-4 mr-2" /> New bill
          </Button>
        </Link>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
        <Card className="border-slate-200 card-hover" data-testid="summary-paid">
          <CardContent className="p-6">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center mb-4">
              <IndianRupee className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
              {formatINR(summary.paid_total)}
            </div>
            <div className="text-sm text-slate-500 mt-1">Total revenue (paid)</div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 card-hover" data-testid="summary-unpaid">
          <CardContent className="p-6">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-4">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
              {formatINR(summary.unpaid_total)}
            </div>
            <div className="text-sm text-slate-500 mt-1">Outstanding</div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 card-hover" data-testid="summary-count">
          <CardContent className="p-6">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-4">
              <TrendingUp className="w-5 h-5 text-blue-600" />
            </div>
            <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
              {summary.invoice_count}
            </div>
            <div className="text-sm text-slate-500 mt-1">Total invoices</div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly revenue */}
      {summary.monthly.length > 0 && (
        <Card className="border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Monthly revenue</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {summary.monthly.map((m) => {
                const max = Math.max(...summary.monthly.map((x) => x.revenue));
                const pct = (m.revenue / max) * 100;
                return (
                  <div key={m.month}>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-slate-700">{m.month}</span>
                      <span className="text-sm font-semibold text-slate-900">{formatINR(m.revenue)}</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-sky-400" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoices */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Invoices</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {invoices.length === 0 ? (
            <div className="p-16 text-center" data-testid="invoices-empty-state">
              <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
                <Receipt className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: "Manrope" }}>
                No invoices yet
              </h3>
              <p className="text-sm text-slate-500 mb-5 max-w-sm mx-auto">
                Invoices are auto-generated when you record a patient visit.
              </p>
              <Link to="/patients">
                <Button variant="outline">
                  Go to patients
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-4 sm:mx-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">
                      #{inv.invoice_id.slice(-8)}
                    </TableCell>
                    <TableCell className="font-medium">{inv.patient_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {new Date(inv.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                    </TableCell>
                    <TableCell className="text-right font-semibold">{formatINR(inv.amount)}</TableCell>
                    <TableCell>
                      {inv.status === "paid" ? (
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Paid</Badge>
                          {inv.payment_method && (
                            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium" data-testid={`method-${inv.invoice_id}`}>
                              {inv.payment_method === "bank_transfer" ? "BANK" : inv.payment_method}
                            </span>
                          )}
                        </div>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Unpaid</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {inv.status !== "paid" && upiVpa && upiVpa.includes("@") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setQrInvoice(inv)}
                            title="Show UPI QR"
                            data-testid={`upi-qr-${inv.invoice_id}`}
                            className="h-8 w-8 p-0 text-violet-600 hover:bg-violet-50 hover:text-violet-700"
                          >
                            <ScanLine className="w-4 h-4" />
                          </Button>
                        )}
                        {waConfigured && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => wa.sendInvoice(inv.invoice_id)}
                            title="Auto-send invoice via WhatsApp"
                            data-testid={`whatsapp-auto-invoice-${inv.invoice_id}`}
                            className="h-8 w-8 p-0 text-green-700 hover:bg-green-50 hover:text-green-800"
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => sendInvoiceWhatsApp(inv)}
                          title="Open WhatsApp on this device"
                          data-testid={`whatsapp-invoice-${inv.invoice_id}`}
                          className="h-8 w-8 p-0 text-green-600 hover:bg-green-50 hover:text-green-700"
                        >
                          <MessageCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => printInvoice(inv.invoice_id)}
                          title="Print invoice"
                          data-testid={`print-${inv.invoice_id}`}
                          className="h-8 w-8 p-0"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => duplicateInvoice(inv)}
                          title="Duplicate invoice"
                          data-testid={`duplicate-invoice-${inv.invoice_id}`}
                          className="h-8 w-8 p-0"
                        >
                          <FilePlus className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyShareLink(inv.invoice_id)}
                          title="Copy public share link"
                          data-testid={`share-${inv.invoice_id}`}
                          className="h-8 w-8 p-0"
                        >
                          {copiedId === inv.invoice_id ? <Check className="w-4 h-4 text-emerald-600" /> : <Share2 className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadPdf(inv.invoice_id)}
                          title="Download PDF"
                          data-testid={`pdf-${inv.invoice_id}`}
                          className="h-8 w-8 p-0"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => downloadDocx(inv.invoice_id)}
                          title="Download DOCX"
                          data-testid={`docx-${inv.invoice_id}`}
                          className="h-8 px-2 text-xs font-semibold text-slate-500"
                        >
                          .docx
                        </Button>
                        <Button
                          size="sm"
                          variant={inv.status === "paid" ? "outline" : "default"}
                          onClick={() => togglePaid(inv)}
                          className={inv.status === "paid" ? "ml-1" : "bg-blue-600 hover:bg-blue-700 ml-1"}
                          data-testid={`toggle-paid-${inv.invoice_id}`}
                        >
                          {inv.status === "paid" ? "Mark unpaid" : "Mark paid"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment method picker — opens when user clicks "Mark paid" on an unpaid invoice */}
      <Dialog open={!!payMethodFor} onOpenChange={(o) => { if (!o) setPayMethodFor(null); }}>
        <DialogContent className="max-w-sm" data-testid="pay-method-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope" }}>Collect payment</DialogTitle>
            <DialogDescription>
              {payMethodFor?.patient_name} · {formatINR(payMethodFor?.amount || 0)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <MethodPicker
              value=""
              onChange={(method) => markPaidWithMethod(payMethodFor, method)}
              testidPrefix="row-pay-method"
            />
            <div className="text-xs text-slate-400 text-center mt-3">
              Tap a method to mark this bill as paid.
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* UPI QR dialog for unpaid invoices */}
      <UpiQrDialog
        open={!!qrInvoice}
        onOpenChange={(o) => { if (!o) setQrInvoice(null); }}
        invoice={qrInvoice}
        upiVpa={upiVpa}
        upiPayeeName={upiPayeeName}
        onPaid={() => { setQrInvoice(null); load(); }}
      />
    </div>
  );
}
